// =============================================================================
// CANVAS CONSTANTS
// =============================================================================

import { BlockDefinition, Block, SlotType, DetailerType, StickyNoteColor } from '../types';

// Block dimensions — issue #59 : une seule image 9:16 par bloc (le prompt
// sélectionné) au lieu de la grille 3×3. Revue UI 12/10 : le titre est sorti
// du bloc (pill externe en haut à gauche) et l'image remplit TOUT le cadre —
// 180 de large → 320 de haut (9/16 exact, plus aucune marge ni header).
export const BLOCK_W = 180;
export const BLOCK_H = 320;

// Sticky note dimensions (post-it carré)
export const STICKY_NOTE_W = 240;
export const STICKY_NOTE_H = 240;

// Couleurs de post-it (données métier — exception tokens AGENTS.md §9.3).
// Pastels lisibles avec un texte foncé assorti (STICKY_NOTE_TEXT_COLORS) ;
// exception : darkBlue (issue #61), fond foncé → texte clair.
export const STICKY_NOTE_COLORS: Record<StickyNoteColor, string> = {
  blue: '#8EC9FF',
  pink: '#FFB3D9',
  green: '#B8EBA0',
  yellow: '#FFE066',
  violet: '#D9B8FF',
  darkBlue: '#1F4E79',
};

// Couleur du texte par couleur de post-it : variante foncée assortie
// (rose foncé sur rose, violet foncé sur violet, etc.), texte clair sur
// darkBlue (issue #61).
export const STICKY_NOTE_TEXT_COLORS: Record<StickyNoteColor, string> = {
  blue: '#1F4E79',
  pink: '#8F1D56',
  green: '#3F6B1F',
  yellow: '#6B5300',
  violet: '#5B2A8C',
  darkBlue: '#F0F6FF',
};
export const DEFAULT_STICKY_NOTE_COLOR: StickyNoteColor = 'blue';

// FreeText block dimensions — horizontal node shape (chainable).
// Même hauteur que les blocs standards (revue utilisateur) — largeur
// conservée plus généreuse pour le texte libre.
export const FREETEXT_BLOCK_W = 300;
export const FREETEXT_BLOCK_H = BLOCK_H;

// Bloc « character-lora » (feature « Character LoRA ») : chaînable
// (ports gauche/droite comme freetext) — titre en haut (icône du kind + nom),
// cover en dessous, œil blanc sur filigrane noir au survol de l'image
// (comme les blocs standards). Même largeur qu'un bloc standard (BLOCK_W —
// revue PO), hauteur généreuse pour la cover.
export const CHARACTER_BLOCK_W = 180;
export const CHARACTER_BLOCK_H = 220;

export { SLOT_ICONS } from './slotIcons';
export { DETAILER_ICONS } from './detailerIcons';

// Slot color (gris clair, aligné sur les blocs standards) — issue #23
export const SLOT_COLOR = '#B3B3B3';

// Canvas settings
// Seuil de bascule en mode skeleton au dézoom : abaissé (0.22 → 0.12, revue PO)
// pour que les blocs ne deviennent gris qu'à un dézoom prononcé (MIN_ZOOM = 0.1).
export const SKELETON_THRESHOLD = 0.12;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 4;
export const AUTOSAVE_DELAY = 2000;

// Slot definitions (issue #23)
export interface SlotDefinition {
  type: SlotType;
  label: string;
  children: Block['type'][];
}

export const SLOT_TYPES: SlotDefinition[] = [
  {
    type: 'character',
    label: 'Character',
    // 'gender' en tête : l'ordre pilote l'ordre des segments dans composePrompt
    // (« woman, 25 years old, … ») et celui des sous-dossiers Character de /explore.
    children: ['gender', 'ethnicity', 'age', 'skin_tone', 'skin_texture', 'hair_color', 'hair_style', 'eye_shape', 'eye_color', 'body_type', 'makeup', 'signature_marks'],
  },
  {
    type: 'outfit',
    label: 'Outfit',
    children: ['top_long_sleeve', 'top_short_sleeve', 'top_lingerie', 'bottom_long', 'bottom_short', 'bottom_lingerie', 'socks', 'set_daywear', 'set_lingerie', 'shoes'],
  },
  {
    // Slot accessoires (issue #51) — 14 blocs, pluriel réservé aux paires (glasses, earrings, gloves)
    type: 'accessory',
    label: 'Accessories',
    children: ['bag', 'hat', 'hairwear', 'glasses', 'mask', 'earrings', 'necklace', 'bracelet', 'ring', 'watch', 'body_jewelry', 'belt', 'gloves', 'neckwear', 'tech'],
  },
  {
    type: 'pose',
    label: 'Pose',
    children: ['position', 'gesture', 'motion'],
  },
  {
    type: 'emotion',
    label: 'Emotion',
    children: ['emotion'],
  },
  {
    type: 'location',
    label: 'Environment',
    // 'zone' : catégorie de prompts à part entière (visible dans /explore et
    // /add-prompt) — le BLOC zone reste créé automatiquement sous chaque
    // housing (jamais dans le dock ni les pickers, cf. ChildBlockPickerOverlay).
    // 'hospitality' : bloc standard SANS le sous-bloc zone (réservé au
    // housing) ni detailers (réservés aux vêtements) — mécanismes câblés sur
    // type === 'housing' / isGarmentType, rien à exclure ici.
    // 'retail' : idem.
    // 'transport', 'sport_wellness', 'culture_nightlife', 'work_study',
    // 'public_facilities', 'urban_outdoor', 'nature' : idem.
    children: ['housing', 'hospitality', 'retail', 'transport', 'sport_wellness', 'culture_nightlife', 'work_study', 'public_facilities', 'urban_outdoor', 'nature', 'zone'],
  },
  {
    type: 'light',
    label: 'Light',
    // Bloc unique « Light » (fusion Light Type + Light Mood)
    children: ['light'],
  },
  {
    type: 'style',
    label: 'Style',
    children: ['render_medium', 'aesthetic', 'color_palette'],
  },
  {
    type: 'photography',
    label: 'Photography',
    // Issue #63 : shot_type/framing (cadrage, ex-shot_size) en tête —
    // l'ordre pilote l'ordre des segments dans composePrompt.
    children: ['shot_type', 'framing', 'device', 'lens', 'image_texture'],
  },
];

export const SLOT_CHILDREN: Record<SlotType, Block['type'][]> = SLOT_TYPES.reduce(
  (acc, slot) => {
    acc[slot.type] = slot.children;
    return acc;
  },
  {} as Record<SlotType, Block['type'][]>
);

/** Type de slot standard (liste fermée) ? Un slot personnel
 *  (issue #66) a pour connectorType l'ID de son doc customConnectors. */
export const isStandardSlotType = (type: string | undefined): type is SlotType =>
  SLOT_TYPES.some((c) => c.type === type);

// =============================================================================
// DETAILERS (issue #55) — blocs color/texture/pattern rattachés à un vêtement
// par un wire (vêtement → detailer). Liste PARALLÈLE à SLOT_TYPES : un
// detailer n'est ni un slot ni un fils de slot (isConnector: false,
// pas de parentConnectorId).
// =============================================================================

export interface DetailerDefinition {
  type: DetailerType;
  label: string;
}

// L'ordre de la liste EST l'ordre de fusion dans le prompt généré :
// color → texture → pattern, avant le texte du vêtement.
export const DETAILER_TYPES: DetailerDefinition[] = [
  { type: 'color', label: 'Color' },
  { type: 'texture', label: 'Texture' },
  { type: 'pattern', label: 'Pattern' },
];

/** Type detailer ? (garde de type — accepte les types inconnus/undefined). */
export const isDetailerType = (type: string | undefined): type is DetailerType =>
  DETAILER_TYPES.some((d) => d.type === type);

// Nombre max de detailers DU MÊME TYPE câblés à un vêtement (revue PO) :
// plusieurs detailers autorisés par vêtement, mais plafonnés pour éviter
// les bugs d'empilement — le store refuse au-delà, les pickers grisent.
export const MAX_DETAILERS_PER_TYPE = 3;

// Bloc « zone » (feature « Housing types & bloc Zone ») : sous-bloc
// automatique d'un bloc housing, rattaché par un wire (housing → zone) —
// créé par le store uniquement (jamais dans le dock ni les pickers), mais sa
// catégorie de prompts EST fille du slot location (pool du bloc :
// promptsByCategory['zone'], comme n'importe quel bloc à prompts).
export const ZONE_TYPE = 'zone';

/** Type « zone » ? (accepte les types inconnus/undefined). */
export const isZoneType = (type: string | undefined): boolean => type === ZONE_TYPE;

// Vêtements/accessoires pouvant recevoir des detailers : fils de outfit/accessory
// (les types de blocs sont propres à un seul slot — aucun recouvrement).
const GARMENT_TYPES: ReadonlySet<string> = new Set<string>([
  ...SLOT_CHILDREN.outfit,
  ...SLOT_CHILDREN.accessory,
]);

/** Type de bloc « vêtement » (fils de outfit/accessory) ? */
export const isGarmentType = (type: string | undefined): boolean =>
  !!type && GARMENT_TYPES.has(type);

// Generic SVG used for slot children without a dedicated icon asset
const GENERIC_CHILD_ICON = '/assets/Apps/layers.svg';
const GENERIC_CHILD_ICON_HOVER = '/assets/Apps/hover/layershover.svg';

// Block types with icons and hover variants
// Only slot children, free-text and sticky-note are shown in the dock.
// Legacy clothing/non-clothing blocks are kept for label/icon retro-compatibility.
export const BLOCK_TYPES: BlockDefinition[] = [
  // Slot children (issue #23)
  { type: 'gender', label: 'Gender', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'ethnicity', label: 'Ethnicity', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'age', label: 'Age', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'skin_tone', label: 'Skin Tone', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'skin_texture', label: 'Skin Texture', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'hair_color', label: 'Hair Color', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'hair_style', label: 'Hair Style', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'eye_shape', label: 'Eye Shape', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'eye_color', label: 'Eye Color', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'body_type', label: 'Body Type', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'makeup', label: 'Makeup', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'signature_marks', label: 'Signature Marks', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'top_long_sleeve', label: 'Top Long Sleeve', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'top_short_sleeve', label: 'Top Short Sleeve', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'top_lingerie', label: 'Top Lingerie', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'bottom_long', label: 'Bottom Long', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'bottom_short', label: 'Bottom Short', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'bottom_lingerie', label: 'Bottom Lingerie', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'socks', label: 'Socks', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'set_daywear', label: 'Set Daywear', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'set_lingerie', label: 'Set Lingerie', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'shoes', label: 'Shoes', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  // Accessoires (issue #51)
  { type: 'bag', label: 'Bag', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'hat', label: 'Hat', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'hairwear', label: 'Hairwear', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'glasses', label: 'Glasses', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'mask', label: 'Mask', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'earrings', label: 'Earrings', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'necklace', label: 'Necklace', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'bracelet', label: 'Bracelet', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'ring', label: 'Ring', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'watch', label: 'Watch', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'body_jewelry', label: 'Body Jewelry', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'belt', label: 'Belt', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'gloves', label: 'Gloves', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'neckwear', label: 'Neckwear', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'tech', label: 'Tech', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'position', label: 'Position', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'gesture', label: 'Gesture', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'motion', label: 'Motion', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'emotion', label: 'Emotion', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'housing', label: 'Housing', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'hospitality', label: 'Hospitality', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'retail', label: 'Retail', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'transport', label: 'Transport', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'sport_wellness', label: 'Sport & Wellness', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'culture_nightlife', label: 'Culture & Nightlife', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'work_study', label: 'Work & Study', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'public_facilities', label: 'Public Facilities', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'urban_outdoor', label: 'Urban Outdoor', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'nature', label: 'Nature', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'light', label: 'Light', icon: '/assets/Apps/light.svg', iconHover: '/assets/Apps/hover/lighthover.svg' },
  { type: 'render_medium', label: 'Render Medium', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'aesthetic', label: 'Aesthetic', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'color_palette', label: 'Color Palette', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'shot_type', label: 'Shot Type', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'framing', label: 'Framing', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'device', label: 'Device', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'lens', label: 'Lens', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'image_texture', label: 'Image Texture', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  // Detailers (issue #55) — jamais dans le dock : créés depuis les boutons
  // « + » des blocs vêtements. Présents ici pour les labels (getBlockLabel)
  // et la reconnaissance de types à l'import JSON.
  { type: 'color', label: 'Color', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'texture', label: 'Texture', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'pattern', label: 'Pattern', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  // Bloc « zone » (feature « Housing types & bloc Zone ») — jamais dans le
  // dock ni les pickers : créé automatiquement sous chaque bloc housing. Sa
  // catégorie de prompts est fille du slot location (visible dans
  // /explore et /add-prompt). Présent ici pour le label et la reconnaissance
  // à l'import JSON.
  { type: 'zone', label: 'Zone', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  // Legacy blocks from pre-#50 taxonomy (retained for existing canvas/JSON labels)
  { type: 'background', label: 'Background', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'props', label: 'Props', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'facial_expression', label: 'Facial Expression', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'body_energy', label: 'Body Energy', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'light_source', label: 'Source', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'light_direction', label: 'Direction', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'light_quality', label: 'Quality', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'camera_angle', label: 'Angle', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'depth_of_field', label: 'Depth', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'medium', label: 'Medium', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'genre', label: 'Genre', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'palette', label: 'Palette', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'camera_model', label: 'Camera', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'lens_type', label: 'Lens', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'render_quality', label: 'Quality', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  // Legacy clothing blocks (retained for existing project labels)
  { type: 'top', label: 'Top', icon: '/assets/Apps/layers.svg', iconHover: '/assets/Apps/hover/layershover.svg' },
  { type: 'bottom', label: 'Bottom', icon: '/assets/Apps/panties.svg', iconHover: '/assets/Apps/hover/pantieshover.svg' },
  { type: 'outfit_top', label: 'Top', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'outfit_bottom', label: 'Bottom', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'set', label: 'Set', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'swimwear', label: 'Swimwear', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'lingerie', label: 'Lingerie', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'sportswear', label: 'Sportswear', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'outerwear', label: 'Outerwear', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  { type: 'footwear', label: 'Footwear', icon: GENERIC_CHILD_ICON, iconHover: GENERIC_CHILD_ICON_HOVER },
  // Legacy non-clothing blocks (retained for existing project labels)
  { type: 'haircut', label: 'Haircut', icon: '/assets/Apps/Model.svg', iconHover: '/assets/Apps/hover/modelhover.svg' },
  { type: 'photography', label: 'Photography', icon: '/assets/Apps/colorpalette.svg', iconHover: '/assets/Apps/hover/colorpalettehover.svg' },
  { type: 'video-pose', label: 'Video Pose', icon: '/assets/Apps/pose.svg', iconHover: '/assets/Apps/hover/posehover.svg' },
  { type: 'photo-pose', label: 'Photo Pose', icon: '/assets/Apps/pose.svg', iconHover: '/assets/Apps/hover/posehover.svg' },
  { type: 'mood', label: 'Mood', icon: '/assets/Apps/mood.svg', iconHover: '/assets/Apps/hover/moodhover.svg' },
  { type: 'body', label: 'Body', icon: '/assets/Apps/Model.svg', iconHover: '/assets/Apps/hover/modelhover.svg' },
  // Ex-enfant du slot Environment, remplacé par 'housing' (rétrocompat
  // des canvas/prompts existants de catégorie 'location')
  { type: 'location', label: 'Environment', icon: '/assets/Apps/location.svg', iconHover: '/assets/Apps/hover/locationhover.svg' },
];
